import { KeyboardEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUp, ChevronRight, PanelLeftClose, RotateCcw, Sparkles, Square, Undo2 } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { INPUT_SHEET_FILE } from '@/reports/bundles/inputSheet';
import { ReportBundleFiles } from '@/reports/bundles/types';
import { AssistantContentBlock } from '@/types/reportAssistantTypes';
import { cn } from '@/frontend/lib/utils';
import { Button } from '../../ui/button';
import { IconButton, StatusDot } from '../studioChrome';
import { AssistantMarkdown } from './AssistantMarkdown';
import { lineDiff } from './lineDiff';
import { BundleAssistant, LiveBlock, ToolState, isStudioNote } from './useBundleAssistant';

interface BundleAssistantPanelProps {
  assistant: BundleAssistant;
  /** null while the server is asked whether the assistant is set up */
  status: { enabled: boolean; model: string | null } | null;
  files: ReportBundleFiles;
  /** what the preview reads, as the composer's footer names it */
  previewLabel: string;
  onClose: () => void;
  /** a schema's own page: its words, not a report's */
  subject?: 'bundle' | 'schema';
}

const sameFiles = (a: ReportBundleFiles, b: ReportBundleFiles) => Object.keys({ ...a, ...b }).every((p) => a[p] === b[p]);

/**
 * The assistant, the studio's left-hand column: the conversation, each tool call as one
 * line that opens onto what it did (an edit's diff, what a compile or a
 * preview said), and the composer. Enter sends; Shift+Enter breaks the line.
 */
export function BundleAssistantPanel({ assistant, status, files, previewLabel, onClose, subject = 'bundle' }: BundleAssistantPanelProps) {
  const [prompt, setPrompt] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { history, tools, checkpoints, notices, running, live } = assistant;
  const enabled = status?.enabled ?? false;

  // follow the conversation while the author is at its foot
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  });

  useEffect(() => {
    if (enabled) inputRef.current?.focus();
  }, [enabled]);

  const send = () => {
    if (!prompt.trim() || running || !enabled) return;
    assistant.send(prompt);
    setPrompt('');
    pinnedRef.current = true;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const rows: ReactNode[] = [];
  const noticesAt = (n: number) => notices.filter((x) => x.after === n).forEach((x, i) => rows.push(<Notice key={`notice-${n}-${i}`} tone={x.tone}>{x.text}</Notice>));
  history.forEach((message, index) => {
    noticesAt(index);
    if (message.role === 'user') {
      const typed = message.content.filter((b): b is Extract<AssistantContentBlock, { type: 'text' }> => b.type === 'text' && !isStudioNote(b));
      if (typed.length === 0) return;
      const checkpoint = checkpoints[index];
      const canRestore = !running && checkpoint !== undefined && !sameFiles(checkpoint, files);
      rows.push(
        <div key={`u-${index}`} className="group/user ml-6 flex flex-col items-end gap-1">
          <div className="rounded-xl rounded-br-sm bg-muted px-3 py-2 whitespace-pre-wrap [overflow-wrap:anywhere]">{typed.map((b) => b.text).join('\n\n')}</div>
          {canRestore && (
            <Button variant="ghost" size="xs" className="text-muted-foreground" onClick={() => assistant.restore(index)} title={I18n.get('reportAssistantRestoreHelp')}>
              <Undo2 /> {I18n.get('reportAssistantRestore')}
            </Button>
          )}
        </div>,
      );
      return;
    }
    message.content.forEach((block, b) => {
      const key = `a-${index}-${b}`;
      if (block.type === 'text' && block.text.trim()) rows.push(<div key={key} className="[overflow-wrap:anywhere]"><AssistantMarkdown text={block.text} /></div>);
      else if (block.type === 'thinking' && block.thinking.trim()) rows.push(<Thinking key={key} text={block.thinking} />);
      else if (block.type === 'tool_use') rows.push(<ToolRow key={key} state={tools[block.id] ?? { verb: block.name, status: 'done' }} />);
    });
  });
  noticesAt(history.length);

  return (
    <aside className="flex h-full flex-col bg-background text-sm" aria-label={I18n.get('reportAssistant')}>
      <div className="flex h-[2.5rem] shrink-0 items-center justify-between gap-2 border-b px-3">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="size-4 shrink-0 text-primary" />
          <span className="font-medium">{I18n.get('reportAssistant')}</span>
          {status?.model && <code className="truncate font-mono text-xs text-muted-foreground">{status.model}</code>}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <IconButton label={I18n.get('reportAssistantNewConversation')} size="icon-xs" onClick={assistant.reset} disabled={running || history.length === 0}><RotateCcw /></IconButton>
          <IconButton label={I18n.get('reportBundleHideAssistant')} size="icon-xs" onClick={onClose}><PanelLeftClose /></IconButton>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-3 py-3"
        onScroll={(e) => { const el = e.currentTarget; pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}
      >
        {status && !enabled && <Notice tone="warn">{I18n.get('reportAssistantNotConfigured')}</Notice>}
        {enabled && history.length === 0 && <EmptyState subject={subject} fromInputs={INPUT_SHEET_FILE in (files ?? {})} onPick={(text) => { setPrompt(text); inputRef.current?.focus(); }} />}
        {rows}
        {live && <LiveReply blocks={live} />}
        {running && !live?.length && <div className="flex items-center gap-2 text-xs text-muted-foreground"><StatusDot tone="busy" /> {I18n.get('reportAssistantWorking')}</div>}
      </div>

      <div className="shrink-0 p-2 pt-0">
        <div className={cn('rounded-xl border-[1px] border-input bg-background shadow-xs transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30', !enabled && 'opacity-60')}>
          <textarea
            ref={inputRef}
            className="block max-h-60 min-h-16 w-full resize-none bg-transparent px-3 pt-2.5 pb-1 text-sm outline-none field-sizing-content placeholder:text-muted-foreground disabled:cursor-not-allowed"
            rows={3}
            value={prompt}
            placeholder={I18n.get(subject === 'schema' ? 'schemaAssistantPlaceholder' : 'reportAssistantPlaceholder')}
            aria-label={I18n.get(subject === 'schema' ? 'schemaAssistantPlaceholder' : 'reportAssistantPlaceholder')}
            disabled={!enabled}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <div className="flex items-center justify-between gap-2 py-1.5 pr-1.5 pl-3">
            <span className="truncate text-xs text-muted-foreground" title={previewLabel}>{previewLabel ? I18n.get('reportAssistantPreviewReads', { run: previewLabel }) : ''}</span>
            {running
              ? <Button variant="outline" size="sm" className="shrink-0" onClick={assistant.stop}><Square className="fill-current" /> {I18n.get('reportAssistantStop')}</Button>
              : <Button size="sm" className="shrink-0" onClick={send} disabled={!enabled || !prompt.trim()}><ArrowUp /> {I18n.get('reportAssistantSend')}</Button>}
          </div>
        </div>
      </div>
    </aside>
  );
}

const NOTICE_TONES = {
  info: 'text-muted-foreground',
  warn: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200',
  error: 'bg-red-50 text-red-900 ring-1 ring-red-200',
};

function Notice({ tone, children }: { tone: keyof typeof NOTICE_TONES; children: ReactNode }) {
  return <div className={cn('rounded-lg px-2.5 py-1.5 text-xs', NOTICE_TONES[tone])}>{children}</div>;
}

function EmptyState({ onPick, fromInputs, subject }: { onPick: (text: string) => void; fromInputs: boolean; subject: 'bundle' | 'schema' }) {
  const ideas = subject === 'schema'
    ? [I18n.get('schemaAssistantIdeaSection'), I18n.get('schemaAssistantIdeaRequired'), I18n.get('schemaAssistantIdeaReview')]
    : fromInputs
    ? [I18n.get('rv2AssistantIdeaLimits'), I18n.get('rv2AssistantIdeaInput'), I18n.get('rv2AssistantIdeaCheck')]
    : [I18n.get('reportAssistantIdeaLimits'), I18n.get('reportAssistantIdeaGroups'), I18n.get('reportAssistantIdeaCheck')];
  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex items-start gap-2.5 text-muted-foreground">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Sparkles className="size-3.5" /></span>
        <p className="pt-1">{I18n.get(subject === 'schema' ? 'schemaAssistantIntro' : 'reportAssistantIntro')}</p>
      </div>
      <div className="flex flex-col gap-1.5">
        {ideas.map((idea) => (
          <button key={idea} type="button" className="rounded-lg border-[1px] px-3 py-2 text-left text-foreground/80 transition-colors hover:border-ring hover:bg-muted/50" onClick={() => onPick(idea)}>
            {idea}
          </button>
        ))}
      </div>
    </div>
  );
}

function Thinking({ text, live = false }: { text: string; live?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground italic hover:text-foreground" onClick={() => setOpen(!open)} aria-expanded={open}>
        <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} /> {live ? I18n.get('reportAssistantThinkingNow') : I18n.get('reportAssistantThinking')}
      </button>
      {open && <div className="mt-1 border-l-2 pl-2 text-xs whitespace-pre-wrap text-muted-foreground">{text}</div>}
    </div>
  );
}

const TOOL_DOTS = { running: 'busy', done: 'ok', error: 'bad' } as const;
const BOX = 'mt-1 max-h-88 overflow-auto rounded-md border-[1px] bg-muted/30 px-2 py-1.5 font-mono text-[0.7rem] leading-relaxed whitespace-pre';

function ToolRow({ state }: { state: ToolState }) {
  const [open, setOpen] = useState(false);
  const diff = state.change ? lineDiff(state.change.before, state.change.after) : null;
  const detail = diff ? `+${diff.added} −${diff.removed}` : state.detail;
  const expandable = !!diff || !!state.result;
  return (
    <div>
      <button type="button" className={cn('flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left font-mono text-xs', expandable ? 'hover:bg-muted' : 'cursor-default')} onClick={() => setOpen(!open)} disabled={!expandable} aria-expanded={open}>
        <StatusDot tone={TOOL_DOTS[state.status]} />
        <span className="font-semibold">{state.verb}</span>
        {state.target && <span className="min-w-0 truncate text-primary">{state.target}</span>}
        {detail && <span className={cn('ml-auto whitespace-nowrap', state.status === 'error' ? 'text-destructive' : 'text-muted-foreground')}>{detail}</span>}
      </button>
      {open && diff && (
        <pre className={BOX}>
          {diff.lines.map((l, i) => (l === null
            ? <div key={i} className="text-muted-foreground">⋯</div>
            : <div key={i} className={cn(l.kind, l.kind === 'added' && 'bg-emerald-50 text-emerald-900', l.kind === 'removed' && 'bg-red-50 text-red-900')}><span className="inline-block w-10 text-muted-foreground select-none">{l.line}</span>{l.kind === 'added' ? '+ ' : l.kind === 'removed' ? '− ' : '  '}{l.text}</div>))}
        </pre>
      )}
      {open && !diff && state.result && <pre className={BOX}>{state.result.length > 6000 ? `${state.result.slice(0, 6000)}\n…` : state.result}</pre>}
    </div>
  );
}

function LiveReply({ blocks }: { blocks: LiveBlock[] }) {
  return (
    <>
      {blocks.map((block, i) => {
        if (block.kind === 'thinking') return <Thinking key={i} text={block.text} live={i === blocks.length - 1} />;
        if (block.kind === 'tool_use') return <ToolRow key={i} state={{ verb: block.name ?? '', status: 'running' }} />;
        return block.text ? <div key={i} className="[overflow-wrap:anywhere]"><AssistantMarkdown text={block.text} /></div> : null;
      })}
    </>
  );
}
